import React, { useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { useMutation, useQuery } from '@common/hooks';
import {
  UPDATE_PROFILE_STATUS_MUTATION,
  ProfileStatusSetupDrawerEntityType,
  DO_NOT_REHIRE_QUERY,
} from '@working-experience/graphql';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import {
  ProfileStatusSetupForm,
  ProfileStatusSetupFormValuesType,
} from './_profile-status-setup-form';
import {
  HistoryDropDown,
  Modal,
  Drawer,
  ErrorHandler,
  ButtonGroup,
  Form,
} from '@common/components';
import { useCurrentMembership } from '@identity/hooks';

type WorkerStatusType = 'do_not_rehire' | 'eligible_for_resubmission';

export function ProfileStatusSetupBaseDrawer({
  open,
  onClose,
  entity,
  applicationId,
  assignmentId,
  loading,
  doNotRehireQueryParam,
  assignmentCancellationReason,
}: {
  open: boolean;
  onClose: () => void;
  entity?: ProfileStatusSetupDrawerEntityType;
  applicationId?: string;
  assignmentId?: string;
  loading: boolean;
  doNotRehireQueryParam?: boolean;
  assignmentCancellationReason?: string;
}) {
  const currentMembership = useCurrentMembership();
  const [workerStatus, setWorkerStatus] = useState<WorkerStatusType>();
  const [formChanged, setFormChanged] = useState<boolean>(false);
  const [formHasErrors, setFormHasErrors] = useState<boolean>(false);
  const [form] = Form.useForm();

  const {
    loading: dnrLoading,
    error: dnrError,
    data: dnrData,
  } = useQuery(DO_NOT_REHIRE_QUERY, {
    variables: {
      identificationCode: entity?.worksProfile.identificationCode || '',
      organizationId: entity?.organization.id || '',
      organizationBusinessId: entity?.organizationBusiness.id || '',
      organizationUnitId: entity?.organizationUnit.id || '',
    },
    skip:
      !entity?.worksProfile.identificationCode ||
      !entity?.organization.id ||
      !entity?.organizationBusiness.id ||
      !entity?.organizationUnit.id,
  });

  const status: WorkerStatusType =
    entity?.doNotRehire || doNotRehireQueryParam
      ? 'do_not_rehire'
      : 'eligible_for_resubmission';

  const [updateProfileStatus, { loading: mutationLoading }] = useMutation(
    UPDATE_PROFILE_STATUS_MUTATION,
    {
      onCompleted: (data) => {
        if (data?.updateProfileStatus?.doNotRehire) {
          message.success('Do Not Rehire status has been updated');
          onWorkerStatusChange(status);
          onClose();
        }
        if (data?.updateProfileStatus?.error) {
          addErrorsToForm(form, data.updateProfileStatus.error);
        }
      },
      onError: () => message.error('Error occurred, try again later'),
      refetchQueries: [
        'DoNotRehireQuery',
        'CandidateAssignmentQuery',
        'StaffingRequestApplicationData',
        'ProfileStatusSetupDrawerApplicationQuery',
        'ProfileStatusSetupDrawerAssignmentQuery',
      ],
    }
  );

  useEffect(() => {
    setWorkerStatus(status);
  }, [status]);

  useEffect(() => {
    if (!open) {
      onWorkerStatusChange(status);
      setFormChanged(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, open, status]);

  if (assignmentId && applicationId) {
    const errorMessage =
      'ProfileStatusSetupBaseDrawer: it accepts only applicationId or assignmentId but received both.';
    ErrorHandler.showErrorNotification();
    console.error(errorMessage);
    return null;
  }

  if (dnrError) {
    return (
      <ErrorHandler
        messageType="contextual"
        error={dnrError}
        log="ProfileStatusSetupForm"
      />
    );
  }

  const onSubmit = (values: ProfileStatusSetupFormValuesType) => {
    if (!entity || !entity.worksProfile.identificationCode) return;

    const expiration = values.expirationIndefinite
      ? 'indefinite'
      : `${values.expirationNumber} ${values.expirationPeriod}`;

    updateProfileStatus({
      variables: {
        applicationId: applicationId || undefined,
        assignmentId: assignmentId || undefined,
        identificationCode: entity.worksProfile.identificationCode,
        input: {
          workerStatus: values.workerStatus,
          institutionLevel: values.institutionLevel,
          expiration: expiration,
          reason: values.reason,
        },
      },
    });
  };

  const onWorkerStatusChange = (value: WorkerStatusType) => {
    setWorkerStatus(value);
    setFormHasErrors(false);
    form.setFieldsValue({ workerStatus: value });
  };

  const onFormChange = () => {
    setFormChanged(true);
  };

  return (
    <Drawer
      title={
        entity ? `${entity.worksProfile.fullName}'s Status Setup` : undefined
      }
      open={open}
      onClose={onClose}
      loading={loading || dnrLoading}
      footer={
        <ButtonGroup>
          <Button size="large" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger={workerStatus === 'do_not_rehire'}
            size="large"
            onClick={() => {
              if (workerStatus === 'do_not_rehire') {
                submissionConfirmationModal(form.submit);
              } else {
                form.submit();
              }
            }}
            loading={mutationLoading}
            disabled={
              (status === workerStatus && !formChanged) || formHasErrors
            }
          >
            Save Edits
          </Button>
        </ButtonGroup>
      }
    >
      <>
        <ProfileStatusSetupForm
          form={form}
          entity={entity}
          onSubmit={onSubmit}
          workerStatus={workerStatus}
          onFormChange={onFormChange}
          setFormHasErrors={setFormHasErrors}
          onWorkerStatusChange={onWorkerStatusChange}
          doNotRehireData={dnrData?.doNotRehire}
          doNotRehireQueryParam={doNotRehireQueryParam || false}
          assignmentCancellationReason={assignmentCancellationReason}
        />
        {currentMembership.canShowWorkerStatus &&
          entity?.profileStatusHistory && (
            <HistoryDropDown data={entity.profileStatusHistory} />
          )}
      </>
    </Drawer>
  );
}

function submissionConfirmationModal(onSubmit: () => void) {
  Modal.confirm({
    title:
      "Are you sure you want to update this candidate's Do Not Rehire status?",
    content: 'Please confirm. Supplier will be notified.',
    onOk: onSubmit,
  });
}
